import React, { useRef, useState, useEffect, useCallback } from "react";
import { CgSpinnerTwo } from "react-icons/cg";
import useProfileContext from "../../hooks/useProfileContext";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import axiosInstance from "../../api/axios";
import useOpenModalContext from "../../hooks/useOpenModalContext";
import toast from "react-hot-toast";

const EditProfileForm = () => {
  const { profileData } = useProfileContext();
  const { phone, address, post_code, city, county } = profileData;
  const { setOpenEditProfile } = useOpenModalContext();
  const { handleProfileData } = useProfileContext();

  const firstNameRef = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const [editData, setEditData] = useState({
    phone: phone || "",
    address: address || "",
    post_code: post_code || "",
    city: city || "",
    county: county || "",
  });

  // Places Autocomplete state
  const placesLib = useMapsLibrary("places");
  const [inputValue, setInputValue] = useState(editData.address);
  const [predictions, setPredictions] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const sessionToken = useRef(null);
  const autocompleteService = useRef(null);

  // Initialize the autocomplete service
  useEffect(() => {
    if (!placesLib) return;

    const initializeService = () => {
      autocompleteService.current = new placesLib.AutocompleteService();
      sessionToken.current = new placesLib.AutocompleteSessionToken();
      setIsReady(true);
    };

    initializeService();

    return () => {
      autocompleteService.current = null;
      sessionToken.current = null;
    };
  }, [placesLib]);

  // Fetch predictions when input changes
  useEffect(() => {
    if (!isReady || !inputValue.trim()) {
      setPredictions([]);
      return;
    }

    const getPredictions = async () => {
      try {
        const request = {
          input: inputValue,
          sessionToken: sessionToken.current,
          types: ["geocode"],
          componentRestrictions: { country: "GB" }, // UK restrictions
        };

        const result = await autocompleteService.current.getPlacePredictions(
          request
        );
        setPredictions(result.predictions || []);
      } catch (error) {
        console.error("Autocomplete error:", error);
        setPredictions([]);
      }
    };

    const debounceTimer = setTimeout(getPredictions, 300);
    return () => clearTimeout(debounceTimer);
  }, [inputValue, isReady]);

  // Handle place selection
  const handleSelect = useCallback(
    async (placeId, description) => {
      if (!placesLib) return;

      try {
        // Clear predictions immediately
        setPredictions([]);
        setInputValue(description);

        // Create a new Places Service instance
        const placesService = new placesLib.PlacesService(
          document.createElement("div")
        );

        // Get place details
        const request = {
          placeId,
          fields: ["geometry", "formatted_address", "address_components"],
          sessionToken: sessionToken.current,
        };

        const placeDetails = await new Promise((resolve, reject) => {
          placesService.getDetails(request, (place, status) => {
            if (status === placesLib.PlacesServiceStatus.OK) {
              resolve(place);
            } else {
              reject(new Error(`Place details error: ${status}`));
            }
          });
        });

        // Extract address components
        if (placeDetails) {
          const addressComponents = placeDetails.address_components || [];

          // Helper function to find component by type
          const getComponent = (types) => {
            const component = addressComponents.find((comp) =>
              types.some((type) => comp.types.includes(type))
            );
            return component?.long_name || "";
          };

          // Update form data
          setEditData((prev) => ({
            ...prev,
            address: placeDetails.formatted_address || "",
            post_code: getComponent(["postal_code"]),
            city: getComponent(["locality", "postal_town"]),
            county: getComponent([
              "administrative_area_level_2",
              "administrative_area_level_1",
            ]),
          }));
        }

        // Generate a new session token for the next request
        sessionToken.current = new placesLib.AutocompleteSessionToken();
      } catch (error) {
        console.error("Place details error:", error);
        toast.error("Failed to get place details. Please try again.");
      }
    },
    [placesLib]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));

    // Special handling for address input
    if (name === "address") {
      setInputValue(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const url = "/account/api/v1/service-user/update/profile/";
      const data = {
        post_code: editData.post_code,
        phone: editData.phone,
        city: editData.city,
        county: editData.county,
        address: editData.address,
      };

      const response = await axiosInstance.post(url, data);
      if (response) {
        await toast.promise(
          new Promise((resolve) => {
            handleProfileData();
            setTimeout(resolve, 2000);
          }),
          {
            loading: "Updating profile...",
            success: "Profile updated successfully!",
            error: "Error updating profile",
          },
          {
            success: {
              duration: 3000,
              icon: "🎉",
            },
            error: {
              duration: 3000,
              icon: "❌",
            },
            style: {
              background: "#FFE86E",
              color: "#012332",
            },
          }
        );

        setTimeout(() => {
          setOpenEditProfile(false);
        }, 3000);
      }
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className="w-full space-y-5 relative" onSubmit={handleSubmit}>
      <div className="relative">
        <input
          className="bg-secondary/10 p-5 w-full rounded-md placeholder:text-sm pl-5 outline-none placeholder:text-primary"
          placeholder="Address"
          type="text"
          name="address"
          value={inputValue}
          onChange={handleChange}
          ref={firstNameRef}
          autoComplete="off"
          required
        />

        {predictions.length > 0 && (
          <ul className="space-y-3 text-sm bg-white absolute top-16 z-50 w-full shadow-lg rounded-md max-h-60 overflow-y-auto">
            {predictions.map((prediction) => (
              <li
                className="cursor-pointer p-2 hover:bg-primary/20 rounded-md"
                key={prediction.place_id}
                onClick={() =>
                  handleSelect(prediction.place_id, prediction.description)
                }
              >
                {prediction.description}
              </li>
            ))}
          </ul>
        )}
      </div>

      <input
        className="bg-secondary/10 p-5 w-full rounded-md placeholder:text-sm pl-5 outline-none placeholder:text-primary"
        placeholder="Post Code"
        type="text"
        name="post_code"
        value={editData.post_code}
        onChange={handleChange}
        autoComplete="off"
        required
      />
      <input
        className="bg-secondary/10 p-5 w-full rounded-md placeholder:text-sm pl-5 outline-none placeholder:text-primary"
        placeholder="City"
        type="text"
        name="city"
        value={editData.city}
        onChange={handleChange}
        autoComplete="off"
        required
      />
      <input
        className="bg-secondary/10 p-5 w-full rounded-md placeholder:text-sm pl-5 outline-none placeholder:text-primary"
        placeholder="County"
        type="text"
        name="county"
        value={editData.county}
        onChange={handleChange}
        autoComplete="off"
        required
      />
      <input
        className="bg-secondary/10 p-5 w-full rounded-md placeholder:text-sm pl-5 outline-none placeholder:text-primary"
        placeholder="Phone Number"
        type="text"
        name="phone"
        value={editData.phone}
        onChange={handleChange}
        autoComplete="off"
        required
      />

      <button type="submit" className={`btn-primary`}>
        <div className="flex justify-center items-center">
          {isLoading ? (
            <CgSpinnerTwo className="animate-spin text-2xl" />
          ) : (
            "Save Changes"
          )}
        </div>
      </button>
    </form>
  );
};

export default EditProfileForm;
